import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Receipt, Check, X, ArrowSquareOut, CurrencyGbp } from "@phosphor-icons/react";
import { openAuthFile } from "../lib/fileOpen";

import DateInput from "../components/DateInput";
export default function AdminExpensesPage() {
  const [team, setTeam] = useState([]);
  const [status, setStatus] = useState("all");
  const [operativeId, setOperativeId] = useState("");
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState({ expenses: [], total: 0, totals_by_status: {} });
  const [loading, setLoading] = useState(true);
  const [reviewDlg, setReviewDlg] = useState(null);

  useEffect(() => {
    API.get("/in-house/team").then(({ data }) => setTeam(data.team || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (status && status !== "all") qs.set("status", status);
      if (operativeId && operativeId !== "__all__") qs.set("operative_id", operativeId);
      if (range.start) qs.set("start", range.start);
      if (range.end) qs.set("end", range.end);
      const { data } = await API.get(`/admin/expenses?${qs.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load expenses");
    } finally {
      setLoading(false);
    }
  }, [status, operativeId, range]);

  useEffect(() => { load(); }, [load]);

  const applyDecision = async (expense, decision, note = "") => {
    try {
      await API.patch(`/admin/expenses/${expense.id}`, { status: decision, review_note: note });
      toast.success(decision === "approved" ? "Expense approved" : "Expense rejected");
      setReviewDlg(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
    }
  };

  const t = data.totals_by_status || {};

  return (
    <div className="p-6 space-y-5" data-testid="expenses-dashboard-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Receipt className="h-7 w-7 text-emerald-600" /> Expenses
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Review and action expenses submitted by your in-house team across every job.
        </p>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <TotalCard tone="amber" label="Pending review" amount={t.submitted?.amount || 0} count={t.submitted?.count || 0} />
        <TotalCard tone="green" label="Approved" amount={t.approved?.amount || 0} count={t.approved?.count || 0} />
        <TotalCard tone="red" label="Rejected" amount={t.rejected?.amount || 0} count={t.rejected?.count || 0} />
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[170px]" data-testid="expenses-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="submitted">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[200px]">
          <Label className="text-xs">Operative</Label>
          <Select value={operativeId || "__all__"} onValueChange={(v) => setOperativeId(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="expenses-filter-operative"><SelectValue placeholder="All operatives" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All operatives</SelectItem>
              {team.map((u) => <SelectItem key={u._id} value={u._id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Start</Label>
          <DateInput value={range.start}
                 onChange={(e) => setRange({ ...range, start: e.target.value })}
                 className="w-[150px]" data-testid="expenses-filter-start" />
        </div>
        <div>
          <Label className="text-xs">End</Label>
          <DateInput value={range.end}
                 onChange={(e) => setRange({ ...range, end: e.target.value })}
                 className="w-[150px]" data-testid="expenses-filter-end" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-sm text-slate-400">Loading…</p>
        ) : data.expenses.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500">No expenses found for the selected filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/40 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Submitted</th>
                  <th className="text-left px-4 py-2">Operative</th>
                  <th className="text-left px-4 py-2">Job</th>
                  <th className="text-left px-4 py-2">Category</th>
                  <th className="text-right px-4 py-2">Amount</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.expenses.map((x) => (
                  <tr key={x.id} className="border-t border-border hover:bg-slate-50/50 dark:hover:bg-slate-800/20" data-testid={`expense-row-${x.id}`}>
                    <td className="px-4 py-2 text-slate-500">{new Date(x.submitted_at).toLocaleDateString()} {new Date(x.submitted_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="px-4 py-2 font-medium">{x.operative_name}</td>
                    <td className="px-4 py-2">
                      <Link to={`/${x.entity_type === "emergency_job" ? "emergency-jobs" : "jobs"}/${x.entity_id}`} className="group flex flex-col">
                        <span className="font-medium truncate max-w-[260px] group-hover:text-primary">{x.job_title || "—"}</span>
                        <span className="text-[10px] text-slate-400 uppercase">{x.job_reference}</span>
                      </Link>
                    </td>
                    <td className="px-4 py-2 capitalize text-slate-600">{x.category}</td>
                    <td className="px-4 py-2 text-right font-semibold">£{Number(x.amount).toFixed(2)}</td>
                    <td className="px-4 py-2">
                      <StatusBadge status={x.status} />
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      {x.receipt_url && (
                        <button
                          type="button"
                          onClick={() => openAuthFile(x.receipt_url)}
                          className="inline-flex items-center text-blue-600 hover:underline text-xs mr-3"
                          title="View receipt"
                          data-testid={`expense-view-receipt-${x.id}`}
                        >
                          <ArrowSquareOut className="h-3.5 w-3.5 mr-0.5" /> Receipt
                        </button>
                      )}
                      {x.status === "submitted" && (
                        <>
                          <button onClick={() => applyDecision(x, "approved")} className="text-green-600 hover:text-green-700 mr-2" title="Approve" data-testid={`expense-approve-${x.id}`}>
                            <Check className="inline h-4 w-4" />
                          </button>
                          <button onClick={() => setReviewDlg({ expense: x, decision: "rejected", note: "" })} className="text-red-600 hover:text-red-700" title="Reject" data-testid={`expense-reject-${x.id}`}>
                            <X className="inline h-4 w-4" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reject with note dialog */}
      <Dialog open={!!reviewDlg} onOpenChange={(o) => !o && setReviewDlg(null)}>
        <DialogContent data-testid="expense-reject-dialog">
          <DialogHeader><DialogTitle>Reject expense</DialogTitle></DialogHeader>
          {reviewDlg && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                £{Number(reviewDlg.expense.amount).toFixed(2)} · {reviewDlg.expense.operative_name} · {reviewDlg.expense.description || "(no description)"}
              </p>
              <div>
                <Label>Reason (optional — operative will see this)</Label>
                <Textarea rows={3} value={reviewDlg.note} onChange={(e) => setReviewDlg({ ...reviewDlg, note: e.target.value })}
                          placeholder="e.g. Receipt unreadable, please re-submit" data-testid="expense-reject-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDlg(null)}>Cancel</Button>
            <Button onClick={() => applyDecision(reviewDlg.expense, "rejected", reviewDlg.note)}
                    className="bg-red-600 hover:bg-red-700" data-testid="expense-reject-confirm">
              Reject expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    submitted: "bg-amber-100 text-amber-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
  };
  const label = status === "submitted" ? "Pending" : status.charAt(0).toUpperCase() + status.slice(1);
  return <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${map[status]}`}>{label}</span>;
}

function TotalCard({ tone, label, amount, count }) {
  const toneMap = {
    amber: "border-amber-200 bg-amber-50 dark:bg-amber-950/30 text-amber-800",
    green: "border-green-200 bg-green-50 dark:bg-green-950/30 text-green-800",
    red: "border-red-200 bg-red-50 dark:bg-red-950/30 text-red-800",
  };
  return (
    <div className={`rounded-lg border p-4 ${toneMap[tone]}`} data-testid={`expenses-total-${tone}`}>
      <p className="text-xs uppercase tracking-wide opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-1 flex items-center gap-1">
        <CurrencyGbp className="h-5 w-5" />
        {Number(amount).toFixed(2)}
      </p>
      <p className="text-xs opacity-75 mt-0.5">{count} expense{count === 1 ? "" : "s"}</p>
    </div>
  );
}
